import restClient from 'restClient'
import composeUrlWithQueryParams from 'util/fetch/composeUrlWithQueryParams'
import { generateUUID } from 'util/misc'
import { MountingInputsIntegrationData } from '../../types'

// Default worst-case values for when Dlubal fetch fails, values provided by K2 via email
export const defaultWindSpeedWorstCase = 180
export const defaultSnowLoadWorstCase = 70
export const dlubalTimeoutErrorMessage = 'Dlubal fetch timed out. Populated with worst case scenario.'

// Cache helper functions to prevent invalid states
export type DlubalDataType = 'wind' | 'snow'

export interface DlubalCacheData {
  value: number | null
  success: boolean
}

export function getDlubalCache(type: DlubalDataType): DlubalCacheData | null {
  if (!window.projectForm) return null

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const cacheKey = type === 'wind' ? 'dlubal_us_wind' : 'dlubal_us_snow'
  const resultKey = type === 'wind' ? 'wind_result' : 'snow_result'
  const cached = customData[cacheKey]

  if (!cached) return null

  // Validate cache consistency
  const hasValue = cached[resultKey] !== null && cached[resultKey] !== undefined
  const isSuccess = cached.success === true

  // Prevent invalid states
  if (isSuccess && !hasValue) {
    console.warn(`Invalid cache state: success=true but ${resultKey}=${cached[resultKey]}`)
    return null
  }

  return {
    value: hasValue ? cached[resultKey] : null,
    success: isSuccess,
  }
}

export function setDlubalCacheSuccess(type: DlubalDataType, value: number): void {
  if (!window.projectForm) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const cacheKey = type === 'wind' ? 'dlubal_us_wind' : 'dlubal_us_snow'
  const resultKey = type === 'wind' ? 'wind_result' : 'snow_result'

  // Ensure valid success state: success=true AND value is a number
  if (typeof value !== 'number' || isNaN(value)) {
    console.error(`Invalid value for successful cache: ${value}`)
    return
  }

  const updatedCustomData = {
    ...customData,
    [cacheKey]: {
      [resultKey]: value,
      success: true,
    },
  }

  window.projectForm.change('custom_data', updatedCustomData)
}

export function setDlubalCacheFailure(type: DlubalDataType): void {
  if (!window.projectForm) return

  const formState = window.projectForm.getState()
  const customData = formState.values.custom_data || {}
  const cacheKey = type === 'wind' ? 'dlubal_us_wind' : 'dlubal_us_snow'
  const resultKey = type === 'wind' ? 'wind_result' : 'snow_result'

  // Ensure valid failure state: success=false AND value is null
  const updatedCustomData = {
    ...customData,
    [cacheKey]: {
      [resultKey]: null,
      success: false,
    },
  }

  window.projectForm.change('custom_data', updatedCustomData)
}

// Module-scoped promise cache to prevent concurrent requests
const ongoingRequests = new Map<string, Promise<void>>()

// Helper function to check if wind/snow value is valid
const hasValidValue = (value: any): boolean => {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

// Generic function to fetch Dlubal data
async function fetchDlubalData(config: { cacheKey: string; endpoint: string; actionName: string }) {
  const { cacheKey, endpoint, actionName } = config

  if (!window.projectForm) {
    console.error('ProjectForm not available for Dlubal data fetch')
    return
  }

  // Use helper to check cache
  const dataType: DlubalDataType = cacheKey === 'dlubal_us_wind' ? 'wind' : 'snow'
  const cachedData = getDlubalCache(dataType)

  // If we have valid successful cache, don't fetch
  if (cachedData?.success === true) {
    return
  }

  const formState = window.projectForm.getState()
  const { lat, lon } = formState.values
  if (!lat || !lon) {
    console.error('Project coordinates not available for Dlubal fetch')
    return
  }

  // Create a unique request key based on the endpoint and coordinates
  const requestKey = `${endpoint}-${lat}-${lon}`

  // If there's already an ongoing request for this data, return the existing promise
  if (ongoingRequests.has(requestKey)) {
    console.log(`Dlubal request already in progress for ${actionName}, waiting for completion...`)
    return ongoingRequests.get(requestKey)
  }

  // Create new request promise
  const requestPromise = (async () => {
    try {
      const restClientInstance = restClient(window.API_ROOT + '/api')

      const url = composeUrlWithQueryParams({
        baseUrl: endpoint,
        params: { lat, lon },
      })

      const res = await restClientInstance('CUSTOM_GET', actionName, {
        url,
      })
      if (!res.data?.success) {
        throw Error(`${actionName} failed`)
      }

      // Check cache again after fetch to avoid race conditions
      const finalCachedData = getDlubalCache(dataType)
      if (finalCachedData?.success === true) {
        return
      }

      // Store success result using helper
      const resultKey = dataType === 'wind' ? 'wind_result' : 'snow_result'
      const fetchedValue = res.data.result[resultKey]
      setDlubalCacheSuccess(dataType, fetchedValue)
    } catch (err) {
      console.error(`${actionName} failed\n`, err)

      // Store failure result using helper
      setDlubalCacheFailure(dataType)
    } finally {
      // Clean up the ongoing request from cache
      ongoingRequests.delete(requestKey)
    }
  })()

  // Store the promise to prevent concurrent requests
  ongoingRequests.set(requestKey, requestPromise)

  return requestPromise
}

export async function fetchDlubalSnowData() {
  await fetchDlubalData({
    cacheKey: 'dlubal_us_snow',
    endpoint: 'structural/get_dlubal_us_snow',
    actionName: 'loadDlubalSnowData',
  })
}

export async function fetchDlubalWindData() {
  await fetchDlubalData({
    cacheKey: 'dlubal_us_wind',
    endpoint: 'structural/get_dlubal_us_wind',
    actionName: 'loadDlubalWindData',
  })
}

// Auto-fetch Dlubal data if wind/snow values are not already provided
export async function autoFetchDlubalDataIfNeeded(inputOptions: MountingInputsIntegrationData): Promise<void> {
  const formCustomData = window.projectForm?.getState().values.custom_data || {}

  const dlubalDataConfigs = [
    {
      fieldName: 'windSpeed',
      dataKey: 'dlubal_us_wind',
      resultKey: 'wind_result',
      fetchFunction: fetchDlubalWindData,
      hasValue: hasValidValue(inputOptions.windSpeed),
      isCached: !!formCustomData['dlubal_us_wind']?.['wind_result'],
    },
    {
      fieldName: 'designSnowLoad',
      dataKey: 'dlubal_us_snow',
      resultKey: 'snow_result',
      fetchFunction: fetchDlubalSnowData,
      hasValue: hasValidValue(inputOptions.designSnowLoad),
      isCached: !!formCustomData['dlubal_us_snow']?.['snow_result'],
    },
  ]

  const missingDataConfigs = dlubalDataConfigs.filter((config) => !config.hasValue && !config.isCached)

  if (missingDataConfigs.length === 0) {
    return
  }

  const configsWithRequestIds = missingDataConfigs.map((config) => ({
    ...config,
    requestId: generateUUID(),
  }))

  emitFetchStartEvents(configsWithRequestIds)

  // BATCH APPROACH: Collect all results first, then populate fields together
  // This prevents BOM refresh from triggering re-entry during individual field population
  const fetchResults: Array<{
    config: typeof configsWithRequestIds[0]
    success: boolean
    cachedData?: any
  }> = []

  try {
    // Execute all fetches sequentially and collect results to prevent race conditions
    for (const config of configsWithRequestIds) {
      try {
        await config.fetchFunction()

        const formCustomData = window.projectForm?.getState().values.custom_data || {}
        const cachedData = formCustomData[config.dataKey]

        fetchResults.push({
          config,
          success: !!cachedData?.[config.resultKey],
          cachedData: cachedData?.[config.resultKey],
        })
      } catch (error) {
        fetchResults.push({
          config,
          success: false,
        })
      }
    }

    // Only dispatch completion actions after ALL fetches are done
    // This prevents individual field updates from triggering BOM refresh and re-entry
    fetchResults.forEach(({ config, success, cachedData }) => {
      window.reduxStore?.dispatch({
        type: 'MOUNTING_AUTO_FETCH_COMPLETE',
        dataKey: config.dataKey,
        resultKey: config.resultKey,
        requestId: config.requestId,
        success,
        cachedData,
      })
    })
  } catch (error) {
    console.error('Error during auto-fetch of Dlubal data:', error)
  }
}

function emitFetchStartEvents(dataConfigs: Array<{ dataKey: string; resultKey: string; requestId: string }>) {
  dataConfigs.forEach((config) => {
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_START',
      dataKey: config.dataKey,
      resultKey: config.resultKey,
      requestId: config.requestId,
    })
  })
}
